// src/plugins/autotrack/modules/FormModule.ts

import { BaseTrackingModule } from './BaseTrackingModule';
import { FormConfig, FormInteractionData } from '../types';
import { AutoTrackUtils } from '../utils';

export class FormModule extends BaseTrackingModule {
  private config: Required<FormConfig>;
  private formInteractions = new Map<string, FormInteractionData>();
  private fieldFocusTimes = new Map<string, number>();
  private abandonmentTimers = new Map<string, NodeJS.Timeout>();
  private excludeSelector?: string;
  private includeSelector?: string;

  constructor(config: FormConfig = {}, excludeSelector?: string, includeSelector?: string) {
    super('Form');

    this.config = {
      enabled: true,
      trackSubmissions: true,
      trackFieldInteractions: true,
      trackValidationErrors: true,
      captureFieldValues: false, // Default to false for privacy
      excludeFields: ['password', 'ssn', 'credit-card', 'cvv'],
      trackFormAbandonment: true,
      abandonmentTimeout: 30000, // 30 seconds
      ...config,
    };

    this.excludeSelector = excludeSelector;
    this.includeSelector = includeSelector;
  }

  protected setupTracking(): void {
    if (!this.config.enabled) {
      return;
    }

    // Track form submissions
    if (this.config.trackSubmissions) {
      this.addEventListener(document, 'submit', this.handleFormSubmit.bind(this), true);
    }

    // Track field interactions
    if (this.config.trackFieldInteractions) {
      this.addEventListener(document, 'focus', this.handleFieldFocus.bind(this), true);
      this.addEventListener(document, 'blur', this.handleFieldBlur.bind(this), true);
      this.addEventListener(document, 'change', this.handleFieldChange.bind(this), true);
      this.addEventListener(document, 'input', this.handleFieldInput.bind(this), true);
    }

    // Track validation errors
    if (this.config.trackValidationErrors) {
      this.addEventListener(document, 'invalid', this.handleValidationError.bind(this), true);
    }

    // Track form abandonment
    if (this.config.trackFormAbandonment) {
      this.addEventListener(window, 'beforeunload', this.handlePageUnload.bind(this));
    }
  }

  protected cleanup(): void {
    // Clear all timers
    this.abandonmentTimers.forEach(timer => clearTimeout(timer));
    this.abandonmentTimers.clear();

    // Track any remaining form interactions as abandoned
    this.trackAbandonedForms();

    // Clear data
    this.formInteractions.clear();
    this.fieldFocusTimes.clear();
  }

  private handleFormSubmit = (event: Event): void => {
    const form = event.target as HTMLFormElement;

    if (!this.shouldTrackForm(form)) {
      return;
    }

    const formData = this.getFormData(form);
    const _formKey = this.getFormKey(form);

    // Clear abandonment timer for this form
    const timer = this.abandonmentTimers.get(_formKey);
    if (timer) {
      clearTimeout(timer);
      this.abandonmentTimers.delete(_formKey);
    }

    // Calculate form completion time
    const interaction = this.formInteractions.get(_formKey);
    const completionTime = interaction ? Date.now() - (interaction.timeSpent ?? Date.now()) : 0;

    this.trackEvent({
      eventType: '$form_submit',
      properties: {
        ...formData,
        completion_time: completionTime,
        field_count: form.elements.length,
        page_url: window.location.href,
      },
    });

    // Clean up tracking data for this form
    this.formInteractions.delete(_formKey);
  };

  private handleFieldFocus = (event: Event): void => {
    const field = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

    if (!this.shouldTrackField(field)) {
      return;
    }

    const fieldKey = this.getFieldKey(field);
    const form = field.closest('form');
    const _formKey = form ? this.getFormKey(form) : 'no-form';

    // Record focus time
    this.fieldFocusTimes.set(fieldKey, Date.now());

    // Initialize form interaction tracking
    if (!this.formInteractions.has(_formKey)) {
      this.formInteractions.set(_formKey, {
        formId: form?.id,
        formName: form?.name,
        fieldName: field.name ?? field.id,
        fieldType: field.type ?? field.tagName.toLowerCase(),
        interactionType: 'focus',
        timeSpent: Date.now(),
      });
    }

    // Set up abandonment timer
    this.setupAbandonmentTimer(_formKey, form);

    this.trackEvent({
      eventType: '$field_focus',
      properties: {
        form_id: form?.id,
        form_name: form?.name,
        field_name: field.name ?? field.id,
        field_type: field.type ?? field.tagName.toLowerCase(),
        field_placeholder: (field as HTMLInputElement).placeholder ?? undefined,
        page_url: window.location.href,
      },
    });
  };

  private handleFieldBlur = (event: Event): void => {
    const field = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

    if (!this.shouldTrackField(field)) {
      return;
    }

    const fieldKey = this.getFieldKey(field);
    const focusTime = this.fieldFocusTimes.get(fieldKey);
    const timeSpent = focusTime ? Date.now() - focusTime : 0;

    const form = field.closest('form');

    this.trackEvent({
      eventType: '$field_blur',
      properties: {
        form_id: form?.id,
        form_name: form?.name,
        field_name: field.name ?? field.id,
        field_type: field.type ?? field.tagName.toLowerCase(),
        time_spent: timeSpent,
        field_value_length: this.config.captureFieldValues ? field.value.length : undefined,
        page_url: window.location.href,
      },
    });

    // Clean up focus time tracking
    this.fieldFocusTimes.delete(fieldKey);
  };

  private handleFieldChange = (event: Event): void => {
    const field = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

    if (!this.shouldTrackField(field)) {
      return;
    }

    const form = field.closest('form');
    const fieldValue = this.shouldCaptureFieldValue(field) ? field.value : undefined;

    this.trackEvent({
      eventType: '$field_change',
      properties: {
        form_id: form?.id,
        form_name: form?.name,
        field_name: field.name ?? field.id,
        field_type: field.type ?? field.tagName.toLowerCase(),
        field_value: fieldValue,
        field_value_length: field.value.length,
        page_url: window.location.href,
      },
    });
  };

  private handleFieldInput = (event: Event): void => {
    const field = event.target as HTMLInputElement | HTMLTextAreaElement;

    if (!this.shouldTrackField(field) || field.type === 'password') {
      return;
    }

    // Throttle input events to avoid spam
    const throttledHandler = AutoTrackUtils.throttle(() => {
      const form = field.closest('form');

      this.trackEvent({
        eventType: '$field_input',
        properties: {
          form_id: form?.id,
          form_name: form?.name,
          field_name: field.name ?? field.id,
          field_type: field.type ?? field.tagName.toLowerCase(),
          input_length: field.value.length,
          page_url: window.location.href,
        },
      });
    }, 500);

    throttledHandler();
  };

  private handleValidationError = (event: Event): void => {
    const field = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

    if (!this.shouldTrackField(field)) {
      return;
    }

    const form = field.closest('form');

    this.trackEvent({
      eventType: '$form_validation_error',
      properties: {
        form_id: form?.id,
        form_name: form?.name,
        field_name: field.name ?? field.id,
        field_type: field.type ?? field.tagName.toLowerCase(),
        validation_message: field.validationMessage,
        field_value_length: field.value.length,
        page_url: window.location.href,
      },
    });
  };

  private handlePageUnload = (): void => {
    this.trackAbandonedForms();
  };

  private shouldTrackForm(form: HTMLFormElement): boolean {
    return !AutoTrackUtils.shouldExcludeElement(form, this.excludeSelector, this.includeSelector);
  }

  private shouldTrackField(field: Element): boolean {
    if (
      !field ||
      !(
        field instanceof HTMLInputElement ||
        field instanceof HTMLTextAreaElement ||
        field instanceof HTMLSelectElement
      )
    ) {
      return false;
    }

    // Check if field should be excluded
    if (AutoTrackUtils.shouldExcludeElement(field, this.excludeSelector, this.includeSelector)) {
      return false;
    }

    // Check field type exclusions
    const fieldType = field.type ?? field.tagName.toLowerCase();
    const fieldName = field.name ?? field.id ?? '';

    return !this.config.excludeFields.some(
      excluded => fieldType.includes(excluded) ?? fieldName.toLowerCase().includes(excluded)
    );
  }

  private shouldCaptureFieldValue(
    field: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  ): boolean {
    if (!this.config.captureFieldValues) {
      return false;
    }

    // Never capture sensitive field values
    const sensitiveTypes = ['password', 'email', 'tel', 'ssn', 'credit-card'];
    const fieldType = field.type ?? field.tagName.toLowerCase();
    const fieldName = field.name ?? field.id ?? '';

    return !sensitiveTypes.some(
      sensitive => fieldType.includes(sensitive) ?? fieldName.toLowerCase().includes(sensitive)
    );
  }

  private getFormKey(form: HTMLFormElement): string {
    return form.id ?? form.name ?? AutoTrackUtils.generateSelector(form);
  }

  private getFieldKey(field: Element): string {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return field.id ?? (field as any).name ?? AutoTrackUtils.generateSelector(field);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getFormData(form: HTMLFormElement): Record<string, any> {
    return {
      form_id: form.id ?? undefined,
      form_name: form.name ?? undefined,
      form_action: form.action ?? undefined,
      form_method: form.method ?? undefined,
      form_target: form.target ?? undefined,
    };
  }

  private setupAbandonmentTimer(_formKey: string, form: HTMLFormElement | null): void {
    // Clear existing timer
    const existingTimer = this.abandonmentTimers.get(_formKey);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    // Set new timer
    const timer = setTimeout(() => {
      this.trackFormAbandonment(_formKey, form);
      this.abandonmentTimers.delete(_formKey);
    }, this.config.abandonmentTimeout);

    this.abandonmentTimers.set(_formKey, timer);
  }

  private trackFormAbandonment(_formKey: string, form: HTMLFormElement | null): void {
    const interaction = this.formInteractions.get(_formKey);

    if (interaction) {
      this.trackEvent({
        eventType: '$form_abandon',
        properties: {
          form_id: form?.id,
          form_name: form?.name,
          time_spent: Date.now() - (interaction.timeSpent ?? Date.now()),
          last_field: interaction.fieldName,
          page_url: window.location.href,
        },
      });

      this.formInteractions.delete(_formKey);
    }
  }

  private trackAbandonedForms(): void {
    this.formInteractions.forEach(interaction => {
      this.trackEvent({
        eventType: '$form_abandon',
        properties: {
          form_id: interaction.formId,
          form_name: interaction.formName,
          time_spent: Date.now() - (interaction.timeSpent ?? Date.now()),
          last_field: interaction.fieldName,
          abandonment_reason: 'page_unload',
          page_url: window.location.href,
        },
      });
    });
  }

  /**
   * Get current form interaction statistics
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getFormStats(): Record<string, any> {
    return {
      active_forms: this.formInteractions.size,
      active_timers: this.abandonmentTimers.size,
      abandonment_timeout: this.config.abandonmentTimeout,
    };
  }

  /**
   * Manually track form submission
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public trackManualFormSubmit(formData: Record<string, any>): void {
    if (!this.isActive) {
      return;
    }

    this.trackEvent({
      eventType: '$form_submit',
      properties: {
        ...formData,
        manual_tracking: true,
        page_url: window.location.href,
      },
    });
  }
}
